// CONTRATS D'API — la frontière typée entre le frontend et le serveur.
//
// Ces types décrivent ce que le serveur renvoie RÉELLEMENT (vérifié contre
// `server/src/routes/` et `server/src/data/repository.js`), pas ce qu'on
// aimerait qu'il renvoie. Quand le serveur change, c'est ici qu'on le constate —
// et le compilateur signale alors tous les écrans à corriger. C'est précisément
// ce que l'ancien frontend ne savait pas faire.
//
// NOTE DE FIDÉLITÉ : le portefeuille est servi en FRANÇAIS par le repository
// (`nom`, `projets`, `statut`, `etudes`, `activite`) parce qu'il était pré-mis
// en forme pour l'ancienne interface. On ne renomme pas ici : on décrit
// l'existant. Le renommage éventuel est un changement de contrat serveur, à
// faire côté serveur, avec ses tests.

/** Rôle dans l'organisation — décide de l'accès à tous les projets. */
export type OrgRole = 'admin' | 'member';

/** Rôle dans un projet. Seul `Responsable` (ou un admin) peut écrire. */
export type ProjectRole = 'Responsable' | 'Contributeur' | 'Observateur';

export interface User {
  id: string;
  email: string;
  name: string;
  orgRole: OrgRole;
  organizationId: string;
}

/** Réponse de `GET /api/me`. */
export interface MeResponse {
  ok: true;
  user: User;
  projectIds: string[];
  projectRoles: Record<string, ProjectRole>;
  /** Présent uniquement pour l'exploitant de la plateforme (back-office). */
  superAdmin?: true;
  /** Pendant une maintenance : ce compte garde-t-il l'accès à l'application ? */
  maintenanceBypass?: boolean;
}

/** Un compteur du mode démo : ce qui est utilisé, sur ce qui est compris. */
export interface DemoCounter {
  used: number;
  limit: number;
}

/** Réponse de `GET /api/demo/usage` : les limites d'un compte ouvert depuis /demo. */
export type DemoUsage =
  | { demo: false }
  | { demo: true; projects: DemoCounter; studies: DemoCounter; aiActions: DemoCounter };

/** État PUBLIC du mode maintenance (`GET /api/maintenance`). */
export interface MaintenanceState {
  active: boolean;
  message: string;
  /** Fin prévue, annoncée aux utilisateurs (ISO) ; aucune extinction automatique. */
  until: string | null;
}

/** Un projet tel que servi par `GET /api/bootstrap`. */
export interface Project {
  id: string;
  nom: string;
  statut: string;
  /** Score d'adoption affiché au portefeuille. `null` tant qu'il n'est pas calculé. */
  score: number | null;
  etudes: number | null;
  activite: string | null;
  /** Vrai pour le projet de démonstration. Nommé `real` côté serveur (`is_demo`). */
  real: boolean;
}

export interface Client {
  id: string;
  nom: string;
  projets: Project[];
}

/** Inventaire de ce qu'une suppression de projet détruirait — le PREMIER des
 *  deux temps. Confirmer sans savoir qu'on efface 14 verbatims n'est pas
 *  confirmer. */
export interface ProjectDeletionSummary {
  id: string;
  name: string;
  status: string;
  clientName: string | null;
  counts: {
    studies: number;
    personas: number;
    verbatims: number;
    insights: number;
    tests: number;
    audits: number;
    triangulations: number;
    deliverables: number;
    pillars: number;
    imports: number;
    members: number;
  };
}

/** Corps de `POST /api/clients`. Le serveur estampille l'organisation depuis la
 *  session : on n'envoie donc que le nom. */
export interface NewClient {
  nom: string;
}

/** Corps de `POST /api/projects`. Le client parent doit appartenir au tenant —
 *  le serveur le vérifie et renvoie 404 sinon (aucun oracle cross-tenant). */
export interface NewProject {
  clientId: string;
  nom: string;
  status?: string;
}

export interface TeamMember {
  id: string;
  nom: string;
  email: string;
  initials: string | null;
  color: string | null;
  role: string;
  statut: string;
  lastActive: string;
}

export interface ProjectMembership {
  userId: string;
  role: ProjectRole;
}

/** Réponse de `GET /api/bootstrap` — le portefeuille, déjà borné au périmètre. */
export interface BootstrapResponse {
  ok: true;
  user: User;
  projectRoles: Record<string, ProjectRole>;
  clients: Client[];
  users: TeamMember[];
  projectMembers: Record<string, ProjectMembership[]>;
}

/** Forme d'erreur commune à toutes les routes. */
export interface ApiError {
  ok: false;
  error: string;
  /** Code métier : NO_EVIDENCE, NOT_READY, PILLAR_LIMIT, NO_GENERATOR… */
  code?: string;
}

// ---------------------------------------------------------------------------
// TABLEAU DE BORD
// ---------------------------------------------------------------------------

/** Sens de lecture d'un indicateur, tel que posé par `metrics/present.js`. */
export type Tone = 'good' | 'warn' | 'bad' | 'neutral';

/**
 * Variation entre deux fenêtres de même durée.
 *
 * `pct: null` signifie qu'il n'y a PAS de tendance — la période précédente était
 * vide. Le serveur refuse d'appeler « +100 % » un démarrage, et l'affichage doit
 * respecter cette distinction.
 */
export interface Trend {
  pct: number | null;
  delta: number;
  tone: Tone;
  label: string;
}

export interface Metric {
  key: string;
  label: string;
  /** `null` quand le dénominateur est vide : on n'affiche pas « 0 % » de rien. */
  value: number | null;
  unit?: string;
  detail?: string;
  /** Total de référence, pour les indicateurs de couverture (« 4 / 4 »). */
  of?: number;
  hint?: string;
  tone?: Tone;
  trend?: Trend | null;
}

export type MetricGroupKey = 'evidence' | 'traceability' | 'risk' | 'coverage' | 'evaluation';

export type Metrics = Record<MetricGroupKey, Metric[]> & { periodDays: number };

/** Un des quatre socles conditionnant la construction du tableau d'adoption. */
export interface AdoptionCheck {
  key: 'interviews' | 'measures' | 'insights' | 'personas' | 'pillars';
  label: string;
  have: number;
  need: number;
  ok: boolean;
  missing: number;
  hint: string;
}

export interface AdoptionReadiness {
  ready: boolean;
  checks: AdoptionCheck[];
  /** Progression sur les SOCLES atteints (0–100), pas sur les volumes. */
  progress: number;
  missingLabel: string;
  unsupportedInsights: number;
}

export interface MetricsResponse {
  ok: true;
  metrics: Metrics;
  adoption: AdoptionReadiness;
  /** Compteurs bruts. On n'en type que ce que le menu du projet affiche au bout
   *  de chaque ligne. */
  raw: {
    interviews: { total: number };
    insights: { total: number };
    experiments: { studies: number; tests: number };
    coverage: { personas: number };
    triangulation: { measures: number };
  };
}

/** Où vit vraiment un élément listé derrière un indicateur. Un fait n'a pas de
 *  page à lui : il s'ouvre dans son entretien, à défaut dans son étude. */
export interface MetricItemTarget {
  kind: 'interview' | 'study' | 'test' | 'section';
  /** Identifiant de la fiche — ou le slug de la section pour `section`. */
  id: string;
}

export interface MetricItem {
  id: string;
  title: string;
  sub: string;
  /** `null` : l'élément n'a nulle part où s'ouvrir (un pilier, un fait orphelin). */
  target: MetricItemTarget | null;
}

export interface MetricItemsResponse {
  ok: true;
  key: string;
  title: string;
  /** Phrase à afficher quand la liste est vide. */
  empty: string;
  /** Le vrai total : la liste, elle, est plafonnée par le serveur. */
  total: number;
  items: MetricItem[];
}

// ---------------------------------------------------------------------------
// ADOPTION SCORE
// ---------------------------------------------------------------------------

export interface PillarScore {
  pillarId: string;
  name: string;
  objective: string | null;
  origin: 'ia' | 'manuel';
  weight: number;
  /** `null` = pilier sans aucune preuve rattachée. Ce n'est PAS zéro. */
  score: number | null;
  previousScore: number | null;
  /** `null` tant qu'il n'y a pas eu deux calculs : un démarrage n'est pas une tendance. */
  delta: number | null;
  /** D'où vient la note : la valeur relevée, et la règle qui la convertit. */
  measures?: { name: string; value: number; score: number; rule: string }[];
  confidence: number | null;
  /** Signal critique : plafonne le score GLOBAL, pas la note du pilier. */
  isCapped: boolean;
  sampleSize: number;
  computedAt: string | null;
}

export interface GlobalScore {
  /** Moyenne brute /100 avant plafonnement — reste lisible. */
  raw: number | null;
  /** Note affichée, plafonnée à 68 si un pilier porte un signal critique. */
  val: number | null;
  isCapped: boolean;
  confidence: number | null;
  pillars: number;
  scored: number;
  unscored: number;
}

export interface AdoptionResponse {
  /** Modèle de piliers confirmé, `null` s'il reste à confirmer. */
  model?: PillarModelKey | null;
  ok: true;
  pillars: PillarScore[];
  global: GlobalScore;
  computedAt: string | null;
  readiness: AdoptionReadiness | null;
}

export interface ComputeAdoptionResponse {
  ok: true;
  pillars: PillarScore[];
  global: GlobalScore;
  computed: number;
  readiness: AdoptionReadiness;
}

// ---------------------------------------------------------------------------
// OBJETS DE RECHERCHE
//
// Les colonnes arrivent en snake_case (elles viennent du SQL sans mise en
// forme). On décrit l'existant plutôt que de renommer côté client : un
// renommage est un changement de contrat serveur, à faire là-bas, avec ses tests.
// ---------------------------------------------------------------------------

export type Sentiment = 'pos' | 'neu' | 'neg';
export type Gravity = 'low' | 'med' | 'high';

/** Phase du cycle de vie d'un produit. `null` sur un objet = non classé. */
export type Phase = 'creation' | 'discovery' | 'delivery' | 'run' | 'refonte';

/** Classement d'un objet dans le cycle de vie du produit. */
export interface Lifecycle {
  phase: Phase | null;
  version: string | null;
}

export interface Study extends Lifecycle {
  id: string;
  code: string | null;
  /** Étiquette courte pour la carte (Entretiens, Questionnaire, Audit UX…) —
   *  texte libre, distincte de `method` qui détaille le protocole. */
  type: string | null;
  method: string | null;
  objective: string | null;
  target: string | null;
  context: string | null;
  status: string | null;
  period: string | null;
  created_at: string;
  /** Test dont cette étude est issue. `null` = étude autonome, ou test supprimé
   *  depuis (le lien est délié, jamais l'étude). */
  origin_test_id?: string | null;
  origin_test_code?: string | null;
  origin_test_objective?: string | null;
  /** Protocole propre au type : guide d'entretien, grille d'observation,
   *  questionnaire. `null` pour un test ou un audit (porté par l'objet lié). */
  protocol?: StudyProtocol | null;
  participants?: number | null;
  /** Test par lequel l'étude SE MÈNE (≠ `origin_test_id`, dont elle est issue). */
  test_id?: string | null;
  test_code?: string | null;
  test_objective?: string | null;
  audit_id?: string | null;
  audit_code?: string | null;
  audit_title?: string | null;
  /** L'étude « mesure zéro » du projet : créée d'office, une seule par projet. */
  is_baseline?: boolean;
  /** Instrument standard (`barometre`) : ses questions ne se modifient pas. */
  instrument?: string | null;
  closed_at?: string | null;
}

export type StudyKind = 'entretien' | 'observation' | 'test' | 'questionnaire' | 'audit';
export type AnswerType = 'ouverte' | 'echelle' | 'choix_unique' | 'choix_multiple' | 'oui_non';

export interface ProtocolItem {
  text: string;
  /** Entretien : relances. */
  followups?: string;
  /** Questionnaire : type de réponse et options (« ; »). */
  answerType?: AnswerType;
  options?: string;
}

export interface ProtocolSection {
  title: string;
  items: ProtocolItem[];
}

export interface StudyProtocol {
  durationMin: number | null;
  intro: string;
  closing: string;
  /** Observation : lieux et situations. */
  places?: string;
  sections: ProtocolSection[];
}

/** Étude adossée à un persona — de quoi la NOMMER sur la carte. */
export interface PersonaStudy {
  id: string;
  code: string | null;
  type: string | null;
  objective: string | null;
}

export interface Persona {
  id: string;
  name: string;
  role: string | null;
  pole: string | null;
  description: string | null;
  initials: string | null;
  accent: string | null;
  adoption_score: number | null;
  session_count: number | null;
  nugget_count: number | null;
  seniority?: Seniority | null;
  /** Où en est sa construction (migration 040). */
  status?: PersonaStatus;
  /** Besoins et irritants. `origin: 'ia'` = proposé par l'IA pour guider la
   *  saisie : une hypothèse, jamais une preuve. */
  needs?: PersonaNeed[];
  /** Le terrain sur lequel ce persona repose. Toujours au moins une depuis la
   *  migration 014 — sauf pour les personas créés avant elle. */
  studies: PersonaStudy[];
}

export type PersonaStatus = 'Brouillon' | 'En cours' | 'Validé';
export const PERSONA_STATUSES: PersonaStatus[] = ['Brouillon', 'En cours', 'Validé'];

export type Seniority = 'debutant' | 'confirme' | 'expert' | 'senior';

export interface PersonaNeed {
  type: 'besoin' | 'irritant';
  label: string;
  origin: 'ia' | 'manuel';
}

/** Ce qu'emporte la suppression d'un persona. Les verbatims ne sont PAS
 *  détruits : ils perdent leur attribution. */
export interface PersonaDeletionSummary {
  id: string;
  name: string;
  role: string | null;
  verbatims: number;
  studies: number;
  tests: number;
}

export interface Insight extends Lifecycle {
  id: string;
  pillar_id: string | null;
  severity: string | null;
  title: string;
  summary: string | null;
  recommendation: string | null;
  jira_ref: string | null;
  feature_label: string | null;
  treatment: string | null;
  /** Nombre de faits cités. Zéro = insight non étayé, donc non opposable. */
  evidence_count: number;
  /** L'INTERPRÉTATION (migration 036) : pourquoi les utilisateurs se comportent
   *  ainsi, et ce que cela ouvre comme piste d'amélioration. */
  why: string | null;
  opportunity: string | null;
  /** Nature de la recommandation — à qui elle s'adresse dans l'équipe. */
  recommendation_kind: RecoKind | null;
  /** `ia` : proposé par la dérivation depuis une étude, puis accepté. */
  origin: 'manuel' | 'ia';
  /** Études d'où viennent ses preuves : l'objectif de recherche auquel il répond. */
  studies: { id: string; code: string | null; objective: string | null }[];
  /** Creusé depuis un autre insight (`depth` 1 ou 2), ou insight d'origine (0). */
  parent_id: string | null;
  depth: number;
  /** Ce qui lui manque pour être exploitable — calculé par le serveur. */
  quality: InsightQuality;
  /** Qualification du problème (migration 045), SAISIE ou proposée par l'IA. */
  problem_type: ProblemType | null;
  impact: Impact | null;
  cost: Cost | null;
  /** Fréquence, confiance et priorité — CALCULÉES par le serveur depuis les preuves. */
  problem: InsightProblem;
  /** L'OPPORTUNITÉ (migration 048) : qui on vise, ce qu'on parie, ce qui tranchera. */
  target_persona_ids: string[];
  hypothesis: string | null;
  success_metric: SuccessMetric | null;
  /** Ce qui manque pour qu'elle soit vérifiable — calculé par le serveur. */
  opportunity_check: { ready: boolean; missing: string[] };
  /** L'objectif du projet qu'il sert (migration 049), ou `null`. */
  objective_id: string | null;
  /** Le SUIVI de l'hypothèse (migration 051) : statut, tests qui la vérifient… */
  hypothesis_status: HypothesisStatus;
  test_ids: string[];
  /** … et l'avant/après, calculé par le serveur depuis la mesure suivie. */
  tracking: HypothesisTracking | null;
}

export type HypothesisStatus = 'a_tester' | 'en_test' | 'confirmee' | 'infirmee' | 'abandonnee';

export interface HypothesisOutcome {
  baseline: number;
  current: number;
  target: number;
  direction: 'hausse' | 'baisse';
  delta: number;
  /** Part du chemin faite vers la cible : 1 = atteinte, négatif = recul. */
  progress: number;
  level: 'atteinte' | 'en_progres' | 'sans_effet' | 'recul';
  /** Verdict PROPOSÉ (une personne tranche), ou `null` quand c'est encore ouvert. */
  suggestion: 'confirmee' | 'infirmee' | null;
}

export interface HypothesisTracking {
  status: HypothesisStatus;
  /** Dernière valeur relevée de la mesure suivie. */
  current: { value: number; at: string } | null;
  /** En test, mais aucun relevé de la mesure depuis le lancement. */
  awaiting: boolean;
  outcome: HypothesisOutcome | null;
}

/** Une ligne du journal des décisions : figée au moment de décider. */
export interface HypothesisDecision {
  id: string;
  insight_id: string;
  insight_title: string;
  status_from: HypothesisStatus;
  status_to: HypothesisStatus;
  note: string | null;
  hypothesis: string | null;
  metric: (SuccessMetric & { current?: number | null; outcome?: HypothesisOutcome | null }) | null;
  tests: { id: string; code: string | null; objective: string | null; success_rate: number | null }[];
  decided_at: string;
  decided_by_name: string | null;
}

export type ObjectiveImportance = 'principal' | 'secondaire';

/** Un objectif du projet : ce qui compte pour l'entreprise, et oriente les priorités. */
export interface ProjectObjective {
  id: string;
  project_id: string;
  title: string;
  metric: string | null;
  /** AAAA-MM-JJ. */
  due: string | null;
  importance: ObjectiveImportance;
  /** Insights qui le servent. */
  insight_count: number;
}

export interface ObjectivesResponse {
  objectives: ProjectObjective[];
  canWrite: boolean;
  max: number;
}

/** L'indicateur qui confirmera ou infirmera l'hypothèse. */
export interface SuccessMetric {
  label: string;
  unit: string | null;
  baseline: number | null;
  target: number | null;
  /** AAAA-MM-JJ. */
  due: string | null;
  /** La mesure suivie (historique des mesures) qui alimente l'avant/après. */
  key: string | null;
}

/** Une opportunité PROPOSÉE par l'IA : rien n'est enregistré tant qu'on ne l'a pas relue. */
export interface OpportunityProposal {
  proposal: {
    hypothesis: string | null;
    targetPersonaIds: string[];
    successMetric: SuccessMetric | null;
    rationale: string | null;
  } | null;
  mode: 'ia' | 'fake';
}

export type RecoKind = 'interface' | 'fonctionnalite' | 'processus' | 'contenu' | 'ia_data' | 'autre';
export type ProblemType = 'irritant' | 'besoin' | 'contournement';
export type Impact = 'faible' | 'moyen' | 'fort';
export type Cost = 'petit' | 'moyen' | 'gros';

export interface InsightProblem {
  type: ProblemType | null;
  /** Personas des personnes qui ont parlé, et des tests cités. */
  personas: string[];
  /** Personnes distinctes touchées, sur combien rencontrées (`null` : inconnu). */
  frequency: { touched: number; total: number | null; level: 'faible' | 'moyenne' | 'elevee' };
  /** `null` : aucune preuve. Chaque critère dit s'il est rempli. */
  confidence: { level: 'faible' | 'moyenne' | 'forte' | null; checks: { code: string; ok: boolean; text: string }[] };
  /** L'objectif servi, tel que le calcul l'a pris en compte. */
  objective: { id: string; title: string; importance: ObjectiveImportance } | null;
  /** `null` : pas de preuve, donc pas de priorité. */
  priority: {
    score: number;
    level: 'haute' | 'moyenne' | 'basse';
    parts: { gravity: number; frequency: number; impact: number; cost: number; confidence: number; alignment: number };
    /** Valeurs non saisies, prises au niveau moyen. */
    estimated: string[];
  } | null;
}

export interface InsightQuality {
  /** `bad` : aucune preuve. `warn` : il manque quelque chose. `ok` : complet. */
  level: 'ok' | 'warn' | 'bad';
  flags: { code: string; text: string }[];
}

/** Un insight PROPOSÉ par la dérivation. Rien n'est enregistré tant qu'il n'est
 *  pas accepté ; chaque preuve citée existe réellement dans l'étude. */
export interface InsightProposal {
  key: string;
  title: string;
  summary: string | null;
  why: string | null;
  opportunity: string | null;
  recommendation: string | null;
  recommendationKind: RecoKind | null;
  severity: string | null;
  problemType: ProblemType | null;
  pillarId: string | null;
  evidenceIds: string[];
  evidence: { id: string; text: string; sentiment: Sentiment | null; speaker: string | null; sourceRef: string | null }[];
}

/** Réponse de « Creuser » : UNE proposition, ou `null` si le terrain n'offre rien de plus. */
export interface InsightDeepening {
  proposal: InsightProposal | null;
  mode: 'ia' | 'fake';
  depth: number;
  maxDepth: number;
  verbatims: number;
}

export interface InsightDerivation {
  proposals: InsightProposal[];
  /** `fake` : démonstration locale, sans modèle. */
  mode: 'ia' | 'fake';
  /** Nombre de verbatims lus, et s'il a fallu en laisser de côté. */
  verbatims: number;
  truncated: boolean;
}

/**
 * Un fait cité par un insight. DEUX natures depuis la migration 012 : un
 * verbatim, ou une fonctionnalité mesurée lors d'un test.
 *
 * Le serveur PROJETTE la seconde sur les champs de la première (`text`,
 * `sentiment`, `source_ref`) : l'affichage existant reste juste sans rien
 * savoir. `kind` absent = verbatim, le contrat d'avant.
 */
/** Une preuve DÉTECTÉE par l'assistant, à partir du titre de l'insight. */
export interface EvidenceSuggestion {
  id: string;
  excerpt: string;
  text: string;
  /** Pertinence 0–100 telle que classée par le serveur. */
  relevance: number;
  study: string | null;
  studyId: string | null;
  persona: string | null;
  speaker: string | null;
  sourceRef: string | null;
  sentiment: Sentiment | null;
  gravity: Gravity | null;
  timecode: string | null;
}

export interface EvidenceSuggestionResponse {
  ok: true;
  /** `hybride` = le sens a joué ; `lexical` = mots-clés seuls (pas
   *  d'embeddings configurés) ; `empty` = aucun verbatim ; `idle` = titre trop
   *  court pour classer quoi que ce soit. */
  mode: 'hybride' | 'lexical' | 'empty' | 'idle';
  suggestions: EvidenceSuggestion[];
}

export interface Evidence {
  id: string;
  kind?: 'verbatim' | 'test_feature';
  text: string;
  sentiment: Sentiment | null;
  gravity: Gravity | null;
  source_ref: string | null;
  persona_name?: string | null;
  persona_id?: string | null;
  tags?: string[] | null;
  speaker?: string | null;
  ts_ms?: number | null;
  /** Seulement pour `kind: 'test_feature'` — un chiffre n'a ni locuteur ni
   *  timecode, mais il a un test d'origine et un taux. */
  test_id?: string | null;
  test_objective?: string | null;
  score?: number | null;
}

export interface TestFeature {
  id: string;
  name: string;
  /** Taux de réussite sur 100. */
  score: number;
  pillar_id: string | null;
}

/** État du GUIDE de questions — distinct du statut du test (Planifié / En cours
 *  / Terminé). Un test peut être lancé avec un guide non validé : la validation
 *  trace, elle ne bloque pas (décision produit). L'interface le signale. */
export type GuideStatus = 'brouillon' | 'en_attente' | 'valide' | 'a_revoir';

/** Un item du guide : consigne de tâche, question posée, ou relance. */
export interface TestQuestion {
  id: string;
  position: number;
  section: string | null;
  text: string;
  kind: QuestionKind;
}

export type QuestionKind = 'tache' | 'question' | 'relance';

// ---------------------------------------------------------------------------
// SCÉNARIO DE TEST — l'observation, et le score qui s'en déduit.
// ---------------------------------------------------------------------------

/**
 * Ce qui est arrivé sur une action.
 *
 * QUATRE états, pas deux : la personne qui finit par y arriver après trois
 * tentatives n'a ni réussi ni échoué, et c'est elle qui désigne le point à
 * corriger. `non_teste` n'est pas un échec — la ligne sort du calcul.
 */
export type StepOutcome = 'reussi' | 'difficulte' | 'bloque' | 'non_teste';

/** Une ligne du scénario telle que le serveur la renvoie. */
export interface ScenarioStep {
  id: string;
  position: number;
  action: string;
  outcome: StepOutcome;
  comment: string | null;
  pillar_id: string | null;
}

/** Ligne en cours d'édition : pas encore d'identifiant, le scénario s'enregistre
 *  entier (le serveur supprime puis réinsère), comme le guide. */
export interface ScenarioDraft {
  action: string;
  outcome: StepOutcome;
  comment: string | null;
  pillarId: string | null;
}

/** Question en cours d'édition : pas encore d'identifiant, le guide s'enregistre
 *  entier (le serveur supprime puis réinsère). */
export interface QuestionDraft {
  section: string | null;
  text: string;
  kind: QuestionKind;
}

/** Persona VISÉ par un test, tel que renvoyé avec le test. La fiche en reçoit
 *  plus que la liste (pôle et compteurs) : on ne charge pas des chiffres que
 *  personne n'affiche. */
export interface TestPersonaRef {
  id: string;
  name: string;
  role?: string | null;
  pole?: string | null;
  initials: string | null;
  accent: string | null;
  session_count?: number | null;
  nugget_count?: number | null;
}

/** Ce qu'un test a produit — présents sur la FICHE seulement. */
export interface TestInsightRef {
  id: string;
  title: string;
  severity: string | null;
  treatment: string | null;
  pillar_id: string | null;
}

export interface TestStudyRef {
  id: string;
  code: string | null;
  objective: string | null;
  status: string | null;
  period: string | null;
}

/**
 * Inventaire de ce qu'une suppression de test détruirait.
 *
 * Pas de nom à retaper : cette cérémonie reste réservée aux projets. Ce qui
 * tient lieu de délibération, c'est de LIRE cet inventaire — en particulier
 * `orphanedInsights`, la seule ligne qui engage une conclusion et non des objets.
 */
export interface TestDeletionSummary {
  id: string;
  code: string | null;
  objective: string | null;
  status: string;
  counts: {
    features: number;
    scenarioSteps: number;
    questions: number;
    personas: number;
    citations: number;
    studies: number;
    orphanedInsights: number;
  };
  measured: { score: number | null; count: number };
}

export interface UserTest {
  id: string;
  code: string | null;
  type: string | null;
  objective: string | null;
  target: string | null;
  context: string | null;
  participants: number;
  success_rate: number | null;
  status: string;
  period: string | null;
  product_url: string | null;
  features: TestFeature[];
  /**
   * RÉSULTAT MESURÉ : moyenne des taux réellement relevés, calculée par le
   * serveur. `null` = rien n'a été mesuré — ce n'est PAS zéro.
   *
   * `success_rate` (au-dessus) est le vestige du taux saisi à la main : plus
   * aucun écran ne le lit, il ne sert qu'à ne pas détruire les saisies d'avant.
   */
  measured_score: number | null;
  measured_count: number;
  /** Personas visés — rattachement réel, pas une cible en texte libre. */
  personas: TestPersonaRef[];
  /** La boucle de retour, sur la fiche seulement. */
  insights?: TestInsightRef[];
  studies?: TestStudyRef[];
  guide_status: GuideStatus;
  validator_user_id: string | null;
  /** Nom du valideur DÉSIGNÉ (le rôle). */
  validator_name?: string | null;
  /** Nom de qui a effectivement tranché — un administrateur peut dépanner. */
  guide_decided_by?: string | null;
  guide_decided_by_name?: string | null;
  guide_decided_at?: string | null;
  guide_submitted_at?: string | null;
  guide_comment?: string | null;
  /** Présent dans la LISTE (compte) ; le détail porte les questions elles-mêmes. */
  question_count?: number;
  questions?: TestQuestion[];
  /**
   * LE RÉSULTAT DU TEST. Déduit du scénario par le serveur — réussi = 100,
   * difficulté = 50, bloqué = 0, moyenne des lignes observées. Personne ne le
   * tape. `null` = rien n'a encore été observé, ce n'est PAS zéro.
   */
  scenario_score: number | null;
  /** Lignes réellement observées (hors `non_teste`) et lignes au total : un
   *  score sur 2 lignes sur 12 ne se lit pas comme un score sur 12 sur 12. */
  scenario_observed: number;
  scenario_total: number;
  /** Détail par issue — 66 % venu de deux réussites et un blocage n'appelle pas
   *  le même arbitrage que 66 % venu de trois passages en difficulté. */
  scenario_counts: Record<StepOutcome, number>;
  /** Les lignes elles-mêmes : sur la FICHE seulement. */
  scenario?: ScenarioStep[];
}

export interface Audit {
  id: string;
  code: string | null;
  title: string;
  context: string | null;
  app_state: string | null;
  version: string | null;
  user_journey: string | null;
  analysis_cats: string[];
  status: string;
  audit_date: string | null;
}

export type Concordance = 'concordant' | 'divergent' | 'à creuser';

export interface TriangulationRow {
  id: string;
  /** Libellé libre saisi par l'utilisateur. Peut être vide. */
  pillar_label: string | null;
  pillar_id: string | null;
  /** Nom du pilier réellement rattaché, joint côté serveur. Fait autorité sur
   *  `pillar_label` : c'est lui qui dit sur quelle note la ligne pèse. */
  pillar_name?: string | null;
  /** Note quantitative /10. `null` = mesure non saisie, PAS zéro. */
  score: number | null;
  weight: number;
  qual_summary: string | null;
  qual_sentiment: Sentiment | null;
  source_tool: string | null;
  trend: string | null;
  quant_summary: string | null;
  concordance: Concordance | null;
  /** LA DONNÉE RÉELLE, structurée (migration 015). `metric_value` à `null`
   *  signifie « pas encore mesuré » — jamais zéro. `quant_summary` reste
   *  l'interprétation, en toutes lettres. */
  metric_name: string | null;
  metric_value: number | null;
  metric_unit: string | null;
  metric_period: string | null;
  /** D'où vient chaque jambe de la confrontation. */
  source_study_id: string | null;
  source_test_id: string | null;
}

/** D'où viennent les éléments croisés. Le serveur renvoyait déjà ce champ ; le
 *  type l'ignorait, donc l'interface le jetait en silence. */
export interface TriangulationSource {
  source_kind: 'study' | 'test' | 'external';
  source_ref: string;
}

export interface Triangulation extends Lifecycle {
  id: string;
  objective: string | null;
  context: string | null;
  /** Renseigné quand la triangulation reçoit les imports d'un connecteur. */
  connector_id?: string | null;
  rows: TriangulationRow[];
  sources?: TriangulationSource[];
  created_at: string;
}

export interface Pillar {
  id: string;
  name: string;
  objective: string | null;
  weight: number;
  origin: 'ia' | 'manuel';
  position: number;
  /** Pilier du modèle du projet, non supprimable. */
  is_core: boolean;
}

// ---------------------------------------------------------------------------
// CONTEXTE PRODUIT — l'assistant « Mesure 0 »
//
// Ce que le CLIENT déclare de son produit (description, fonctionnalités, métiers)
// et les piliers d'adoption qui en sont déduits. C'est la référence que les
// études viendront confronter : rien ici n'est une preuve.
// ---------------------------------------------------------------------------

export type FeatureStatus = 'en_production' | 'en_cours' | 'prevue';

export interface ProductInfo {
  description: string | null;
  version: string | null;
  url: string | null;
  sectors: string[];
  /** Phase COURANTE du projet : reçue par défaut par tout nouvel objet. */
  phase?: Phase;
  /** État des lieux déclaré au démarrage. `null` = non renseigné. */
  objective: string | null;
  type: ProductType | null;
  market: ProductMarket | null;
  /** Modèle de piliers appliqué, `null` tant qu'il reste à confirmer. */
  pillarModel: PillarModelKey | null;
  /** Figé par les premières réponses au baromètre. */
  modelLocked: boolean;
  /** Ce qui fonctionne / ne fonctionne pas SELON LE CLIENT : des pistes, pas des preuves. */
  works: string[];
  fails: string[];
}

export type ProductType = 'logiciel_metier' | 'saas' | 'app_mobile' | 'site_web' | 'ecommerce' | 'autre';
export type ProductMarket = 'b2b' | 'b2c' | 'b2b2c' | 'interne';
export type PillarModelKey = 'b2b' | 'b2c' | 'b2e';

/** Un pilier du catalogue : ce que le modèle du produit prévoit de mesurer. */
export interface ModelPillar {
  key: string;
  name: string;
  objective: string;
  heart: string[];
  measures: string[];
}

export interface PillarModel {
  label: string;
  why: string;
  /** Mesures affichées, mais hors score pour ce modèle. */
  excluded: string[];
  pillars: ModelPillar[];
}

export interface PillarCatalog {
  models: Record<PillarModelKey, PillarModel>;
  marketModels: Record<ProductMarket, PillarModelKey>;
  typeMarkets: Partial<Record<ProductType, ProductMarket>>;
  /** Mesures d'usage que l'on peut saisir à la main, en pourcentage. */
  manualMeasures: string[];
}

export interface ProductFeature {
  id: string;
  name: string;
  description: string | null;
  status: FeatureStatus;
  position: number;
}

export interface ProductRole {
  id: string;
  name: string;
  /** `null` = non renseigné, ce qui n'est pas « zéro personne ». */
  headcount: number | null;
  usage: string | null;
  /** Part de l'usage en %, `null` si non renseignée. Toutes les parts d'un
   *  projet totalisent 100, ou aucune n'est renseignée. */
  usageShare: number | null;
  position: number;
}

/** Un pilier propre au projet, tel que servi avec le contexte. */
export interface ContextPillar {
  id: string;
  project_id: string;
  name: string;
  objective: string | null;
  weight: number;
  origin: 'ia' | 'manuel';
  position: number;
  /** Pilier du modèle du projet, non supprimable. Les autres sont d'anciens
   *  piliers, gardés avec leurs preuves. */
  is_core: boolean;
  /** Note 0–10 DÉCLARÉE par le client (mesure 0). Pas une note prouvée. */
  declared_score: number | null;
}

/** Instantané du score déclaré, figé à sa date. */
export interface Mesure0Baseline {
  id: string;
  score: number | null;
  pillars: { pillarId: string; name: string; objective: string | null; weight: number; score: number | null }[];
  computedAt: string;
}

export interface ProductContext {
  projectId: string;
  projectName: string;
  product: ProductInfo;
  features: ProductFeature[];
  roles: ProductRole[];
  pillars: ContextPillar[];
  catalog: PillarCatalog;
  questionnaire: { internalAnswered: number; clientResponses: number };
  declaredScored: number;
  baseline: Mesure0Baseline | null;
  /** Avancement DÉDUIT des données : jamais un drapeau qui pourrait mentir. */
  steps: { product: boolean; features: boolean; roles: boolean; pillars: boolean };
  completed: number;
  total: number;
  /** Projet VIDE : rien de saisi, aucune étude. Il s'ouvre sur le parcours de démarrage. */
  empty: boolean;
  /** L'étude où la mesure zéro se consulte et se modifie. */
  baselineStudyId: string | null;
}

/** Corps de `PUT /api/projects/:id/context`. Une section absente reste inchangée. */
export interface ProductContextSave {
  product?: {
    description?: string; version?: string; url?: string; sectors?: string[];
    objective?: string; type?: ProductType | null; market?: ProductMarket | null; phase?: Phase;
    works?: string[]; fails?: string[];
  };
  features?: { id?: string; name: string; description?: string; status: FeatureStatus }[];
  roles?: { id?: string; name: string; headcount?: number | null; usage?: string; usageShare?: number | null }[];
  pillarScores?: { id: string; declaredScore: number | null }[];
}

export interface Mesure0Question {
  key: string;
  label: string;
}

export type Mesure0Answers = Record<string, string>;

export type InvitationStatus = 'pending' | 'answered' | 'expired' | 'revoked';

export interface Mesure0Invitation {
  id: string;
  name: string | null;
  email: string | null;
  createdAt: string;
  expiresAt: string;
  status: InvitationStatus;
  submittedAt: string | null;
  answers: Mesure0Answers;
}

/** Ce que voit le répondant, sans compte : le nom du projet et SES réponses. */
export interface PublicQuestionnaire {
  /** Qui envoie le questionnaire — le répondant doit savoir à qui il répond. */
  senderName: string | null;
  organizationName: string | null;
  projectName: string;
  respondentName: string;
  questions: Mesure0Question[];
  answers: Mesure0Answers;
  submittedAt: string | null;
  expiresAt: string;
}

/** Ce que l'IA propose après lecture du site. Jamais enregistré tel quel. */
export interface ContextProposal {
  product: Pick<ProductInfo, 'description' | 'version' | 'url' | 'sectors'>;
  features: { name: string; description: string | null; status: FeatureStatus }[];
  roles: { name: string; headcount: number | null; usage: string | null }[];
}

// ---------------------------------------------------------------------------
// COPILOTE
// ---------------------------------------------------------------------------

export interface CopilotSource {
  id: string;
  kind: string;
  excerpt: string;
  persona: string | null;
  study: string | null;
  sourceRef: string | null;
  pillar: string | null;
  sentiment: Sentiment | null;
  severity: string | null;
  relevance: number;
  /** Attribution et horodatage : ce qui rend la citation vérifiable à la source. */
  speaker: string | null;
  timecode: string | null;
  document: string | null;
}

export interface CopilotConfidence {
  value: number;
  label: string;
  level: 'none' | 'low' | 'medium' | 'high';
  sourceCount: number;
}

export interface CopilotAnswer {
  ok: true;
  status: 'ok' | 'refused' | 'no_source';
  answer: string;
  /** `model` = synthèse ; `extractive` = preuves brutes, sans clé LLM. */
  mode?: string;
  warning?: string | null;
  confidence: CopilotConfidence;
  sources: CopilotSource[];
}

// ---------------------------------------------------------------------------
// RECHERCHE (sert notamment à choisir les preuves d'un insight)
// ---------------------------------------------------------------------------

export interface SearchHit {
  kind: 'verbatim' | 'insight' | 'persona' | 'study';
  id: string;
  projectId: string;
  content?: string;
  sourceRef?: string | null;
  tags?: string[];
  sentiment?: Sentiment | null;
  gravity?: Gravity | null;
  personaId?: string | null;
  studyId?: string | null;
  pillarId?: string | null;
  title?: string;
  name?: string;
  code?: string | null;
  objective?: string | null;
}

// ---------------------------------------------------------------------------
// ÉCRITURES
// ---------------------------------------------------------------------------

export interface NewStudy {
  code?: string;
  type?: string;
  method?: string;
  objective?: string;
  target?: string;
  context?: string;
  period?: string;
  status?: string;
  /** Test dont cette étude découle. Un identifiant hors du projet est ignoré
   *  côté serveur — l'étude est créée, sans provenance. */
  originTestId?: string;
  /** Clé de la recommandation du plan d'études dont l'étude est née. */
  planKey?: string;
  participants?: number | null;
  protocol?: StudyProtocol | null;
  /** Étude « Test utilisateur » : le test est créé avec elle. */
  test?: { type?: string; personaIds?: string[]; tasks?: { action: string }[] };
  /** Étude « Audit UX » : l'audit est créé avec elle. */
  audit?: { version?: string; userJourney?: string; auditDate?: string; analysisCats?: string[] };
}

export type StudyPatch = Partial<Pick<Study, 'objective' | 'type' | 'method' | 'target' | 'context' | 'status' | 'period' | 'participants'>> & {
  protocol?: StudyProtocol | null;
};

// ---------------------------------------------------------------------------
// PLAN D'ÉTUDES — synthèse de l'état actuel et études recommandées
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ÉQUIPE DU PROJET (Options)
// ---------------------------------------------------------------------------

export interface TeamPerson {
  userId: string;
  name: string;
  email: string;
  initials: string | null;
  color: string | null;
  active: boolean;
}

export interface ProjectTeamMember extends TeamPerson {
  role: ProjectRole;
  /** `admin` : accès implicite à tous les projets (non modifiable ici).
   *  `member` : accès par appartenance, que l'admin peut changer ou retirer. */
  access: 'admin' | 'member';
  since?: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  name: string | null;
  role: ProjectRole;
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'expired' | 'revoked';
}

export interface ProjectTeam {
  members: ProjectTeamMember[];
  /** Personnes de l'organisation qui n'ont pas encore accès (admins seulement). */
  candidates: TeamPerson[];
  invitations: TeamInvitation[];
  /** Demandes d'accès venues du questionnaire de mesure 0 (admins seulement). */
  accessRequests: { id: string; email: string; name: string | null; createdAt: string }[];
  canManage: boolean;
}

/** Ce que voit une personne invitée, avant d'avoir un compte. */
export interface PublicTeamInvitation {
  organizationName: string;
  projectName: string;
  email: string;
  name: string;
  role: ProjectRole;
}

// ---------------------------------------------------------------------------
// CONNECTEURS DE MESURE (Options)
// ---------------------------------------------------------------------------

export type ConnectorKind = 'matomo' | 'jira' | 'ga4' | 'posthog' | 'lookback';

/** `metrics` : mesures vers la triangulation ; `sessions` : séances vers une étude (Lookback). */
export type ConnectorCapability = 'metrics' | 'sessions';

/** Élément parcouru dans Lookback (projet, round ou séance). */
export interface LookbackItem {
  id: string;
  name: string;
  participant: string | null;
  date: string | null;
  url: string | null;
  /** Séances seulement : déjà importée dans l'organisation. */
  imported?: boolean;
}

export interface LookbackImportResult {
  id: string;
  label: string;
  status: 'importee' | 'deja' | 'sans-transcription' | 'echec';
  interviewId?: string;
  verbatims?: number;
  error?: string;
}

export interface ConnectorField {
  key: string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  /** Valeurs connues proposées à la saisie (ex. PostHog Cloud US / EU). */
  suggestions?: string[];
}

/** Description d'un type de connecteur, servie par le serveur. */
export interface ConnectorType {
  kind: ConnectorKind;
  label: string;
  capability: ConnectorCapability;
  fields: ConnectorField[];
  secretFields: ConnectorField[];
  featureHint: string;
  /** Ce connecteur sait mesurer un entonnoir (PostHog). */
  supportsFunnel: boolean;
}

/** Une étape d'entonnoir : un nom lisible et un motif (événement ou chemin d'URL). */
export interface FunnelStep {
  name: string;
  pattern: string;
}

export interface Connector {
  id: string;
  kind: ConnectorKind;
  label: string;
  /** Jamais de secret ici : seulement la configuration publique. */
  config: Record<string, string> & { featureMap?: Record<string, string>; funnel?: FunnelStep[] };
  hasSecret: boolean;
  periodDays: number;
  lastSyncAt: string | null;
  lastStatus: 'ok' | 'error' | null;
  lastError: string | null;
  lastCount: number | null;
  triangulationId: string | null;
  capability: ConnectorCapability;
  /** Connecteur par autorisation (Lookback) : date de connexion, ou null. */
  connectedAt: string | null;
}

export interface ConnectorMetric {
  key: string;
  name: string;
  value: number;
  unit: string;
  period: string;
  summary?: string;
  featureId?: string;
}

/** Tag de la taxonomie du projet. `uses` = verbatims qui le portent. */
export interface ProjectTag {
  id: string;
  label: string;
  family: string | null;
  description: string | null;
  uses: number;
}

/** Tag posé sur des verbatims mais absent de la taxonomie. `variants` = les
 *  graphies rencontrées (« gants », « Gants »), traitées comme un seul tag. */
export interface UntrackedTag {
  label: string;
  uses: number;
  variants: string[];
}

export interface ProjectTagsResponse {
  tags: ProjectTag[];
  untracked: UntrackedTag[];
  canManage: boolean;
}

export type RecPriority = 'haute' | 'moyenne' | 'basse';

export interface StudyRecommendation {
  /** Clé STABLE du motif (« pillar-proof:PIL_x », « role:magasinier »…). */
  key: string;
  /** Règle qui a produit la recommandation — le « pourquoi » en un mot. */
  rule: string;
  type: string;
  title: string;
  objective: string;
  target: string;
  pillarId?: string;
  pillarName?: string;
  priority: RecPriority;
  /** Les faits qui ont déclenché la recommandation. */
  reasons: string[];
  /** `planned` = une étude a déjà été créée depuis cette recommandation ;
   *  `dismissed` = écartée par l'utilisateur (restaurable). */
  status: 'open' | 'planned' | 'dismissed';
  studyId: string | null;
  /** Vrai si l'utilisateur a reformulé la recommandation. */
  edited?: boolean;
}

export interface PlanFinding {
  tone: 'good' | 'warn' | 'bad' | 'neutral';
  text: string;
}

export interface StudyPlan {
  snapshot: {
    studies: number;
    tests: number;
    personas: number;
    verbatims: number;
    insights: number;
    declaredScore: number | null;
    pillars: number;
    pillarsProven: number;
    pillarsDeclared: number;
  };
  findings: PlanFinding[];
  recommendations: StudyRecommendation[];
}

export interface PlanSynthesis {
  summary: string;
  focus: string;
}

export interface NewPersona {
  name: string;
  role?: string;
  pole?: string;
  description?: string;
  initials?: string;
  accent?: string;
  /** Au moins une étude est EXIGÉE à la création (422 sinon). */
  studyIds?: string[];
  seniority?: Seniority | null;
  needs?: PersonaNeed[];
  status?: PersonaStatus;
}

export interface NewInsight {
  title: string;
  summary?: string;
  severity?: string;
  pillarId?: string;
  recommendation?: string;
  why?: string;
  opportunity?: string;
  recommendationKind?: RecoKind;
  problemType?: ProblemType;
  impact?: Impact;
  cost?: Cost;
  /** `ia` quand l'insight vient d'une proposition acceptée. */
  origin?: 'manuel' | 'ia';
  /** Insight dont celui-ci a ete creuse : le serveur en deduit la profondeur. */
  parentId?: string;
  /** Le serveur refuse en 422 un insight sans AUCUNE preuve — mais la preuve
   *  peut être un verbatim OU une fonctionnalité mesurée. Ce tableau peut donc
   *  être vide si `testFeatureIds` ne l'est pas. */
  verbatimIds: string[];
  /** Fonctionnalités mesurées citées comme preuves (migration 012). */
  testFeatureIds?: string[];
}

/** Une fonctionnalité testée, telle que le serveur la RÉÉCRIT à l'édition.
 *  `writeTestFeatures` supprime puis réinsère : un patch de features doit donc
 *  porter TOUTES les fonctionnalités, pas seulement celles qui changent. */
export interface FeaturePatch {
  name: string;
  /** Taux de réussite sur 100. */
  score: number;
  /** Pilier rattaché — c'est lui qui fait peser ce taux sur une note. `null` =
   *  hors score. Un identifiant hors du projet est ramené à `null` côté serveur. */
  pillarId: string | null;
}

/** Champs modifiables d'un test (clés camelCase attendues par le serveur). */
export interface TestPatch {
  code?: string;
  type?: string;
  objective?: string;
  target?: string;
  context?: string;
  participants?: number;
  successRate?: number | null;
  status?: string;
  period?: string;
  productUrl?: string;
  features?: FeaturePatch[];
  /** Personas visés. Même convention que `features` : ABSENT préserve le
   *  rattachement existant, tableau VIDE l'efface. */
  personaIds?: string[];
  /** Personne désignée pour valider le guide. `null` retire la désignation. */
  validatorUserId?: string | null;
}

/** Champs modifiables d'un audit (clés camelCase attendues par le serveur). */
export interface AuditPatch {
  code?: string;
  title?: string;
  context?: string;
  appState?: string;
  version?: string;
  userJourney?: string;
  analysisCats?: string[];
  status?: string;
  auditDate?: string;
}

/** Champs d'une ligne de triangulation, à la création comme à l'édition. */
export interface TriangulationRowPatch {
  pillarLabel?: string;
  pillarId?: string | null;
  score?: number | null;
  weight?: number;
  qualSummary?: string;
  qualSentiment?: Sentiment | null;
  sourceTool?: string;
  trend?: string;
  quantSummary?: string;
  concordance?: Concordance | null;
  metricName?: string;
  /** Nombre libre : une métrique d'usage n'a pas de domaine connu, le serveur
   *  ne la borne donc pas. `null` = non mesuré. */
  metricValue?: number | null;
  metricUnit?: string;
  metricPeriod?: string;
  /** Étude d'où vient le signal terrain de cette ligne. */
  sourceStudyId?: string | null;
  /** Test d'où provient cette mesure. Enregistre le test comme SOURCE du
   *  croisement dans le même appel — une mesure sans provenance ne se relit pas. */
  sourceTestId?: string | null;
}

export interface IngestResult {
  ok: true;
  jobId?: string;
  /** La source gardée : en mode `manual`, c'est elle qu'on ouvre pour la découper. */
  sourceId?: string;
  count: number;
  deduped?: boolean;
  status: string;
  /** `model` = extraction par le modèle ; `fake` = extracteur de développement ;
   *  `manual` = texte gardé pour être découpé à la main. */
  mode: string;
}

// --- Découper une transcription (migration 047) ------------------------------

/** Une transcription d'une étude. `readable` : son texte est gardé, elle se découpe. */
export interface StudySource {
  id: string;
  origin_label: string | null;
  source_type: string;
  created_at: string;
  readable: boolean;
  verbatims: number;
  interview_id: string | null;
}

/** Un tour de parole : début (ms, ou `null`), locuteur, texte. */
export interface SourceSegment {
  tsMs: number | null;
  speaker: string | null;
  text: string;
}

export interface SourceDetail {
  source: {
    id: string;
    projectId: string;
    studyId: string | null;
    originLabel: string | null;
    sourceType: string;
    createdAt: string;
    interviewId: string | null;
    segments: SourceSegment[];
  };
  /** Les verbatims déjà retenus dans cette transcription. */
  verbatims: {
    id: string;
    text: string;
    sentiment: Sentiment | null;
    gravity: string | null;
    tags: string[] | null;
    speaker: string | null;
    ts_ms: number | null;
    persona_name: string | null;
  }[];
}

/** Un passage suggéré par l'IA ; rien n'est écrit tant qu'on ne le retient pas. */
export interface PassageSuggestion {
  key: string;
  segment: number;
  text: string;
  sentiment: Sentiment;
  gravity: string;
  tags: string[];
  pillarId: string | null;
}

// ---------------------------------------------------------------------------
// LIVRABLES (studio de restitution)
// ---------------------------------------------------------------------------

/** Clés des sections assemblables dans un livrable. Le contenu est LIVE :
 *  le livrable dit QUOI montrer, pas un instantané figé. */
export type SectionKey =
  | 'synthese'
  | 'adoption'
  | 'insights'
  | 'personas'
  | 'triangulation'
  | 'tests'
  | 'decisions';

export interface Deliverable {
  id: string;
  project_id: string;
  title: string;
  sections: SectionKey[];
  created_at: string;
  updated_at: string;
}

// --- Entretiens ---------------------------------------------------------------

export type RecordingStatus = 'none' | 'uploading' | 'ready';
export type TranscriptStatus = 'none' | 'pending' | 'running' | 'done' | 'error';

/** Un entretien d'une étude. La clé de stockage de l'enregistrement ne sort
 *  jamais du serveur : on le lit par `/api/interviews/:id/recording`. */
export interface Interview {
  id: string;
  project_id: string;
  study_id: string;
  study_code: string | null;
  study_objective: string | null;
  participant: string;
  persona_id: string | null;
  persona_name: string | null;
  conducted_on: string | null;
  notes: string | null;
  recording_name: string | null;
  recording_type: string | null;
  recording_size: number | string | null;
  recording_status: RecordingStatus;
  transcript_status: TranscriptStatus;
  transcript_error: string | null;
  transcript_started_at: string | null;
  source_id: string | null;
  verbatim_count: number;
  created_at: string;
  /** Séance importée d'un outil (`lookback:<id>`), et où la revoir. */
  external_ref?: string | null;
  external_url?: string | null;
  /** Mesure zéro : qui a été interrogé. `null` pour un entretien ordinaire. */
  participant_role?: ParticipantRole | null;
  study_is_baseline?: boolean;
  /** Présent sur la fiche seulement. */
  transcript_text?: string | null;
  /** Le rendez-vous programmé depuis la fiche (054), s'il y en a un. */
  invitation_id?: string | null;
  invitation_status?: 'pending' | 'accepted' | 'declined' | null;
  invitation_starts_at?: string | null;
  invitation_slots?: string[] | null;
  invitation_duration_min?: number | null;
  invitation_mode?: 'remote' | 'onsite' | null;
  invitation_location?: string | null;
  invitation_meeting_url?: string | null;
  invitation_provider?: 'google_meet' | 'teams' | null;
  invitation_invitee?: string | null;
}

/** Les quatre synthèses qu'on tire d'un entretien (054). */
export type InterviewOutputFormat = 'transcription' | 'synthese' | 'objections' | 'ux';
export type InterviewTone = 'neutre' | 'professionnel' | 'decontracte' | 'pedagogique';

export interface InterviewOutput {
  id: string;
  format: InterviewOutputFormat;
  tone: InterviewTone;
  content: string;
  created_at: string;
}

export type ParticipantRole = 'dirigeant' | 'product_owner' | 'commanditaire';

export interface InterviewPatch {
  studyId?: string;
  participantRole?: ParticipantRole | null;
  participant?: string;
  personaId?: string | null;
  conductedOn?: string | null;
  notes?: string | null;
}

// --- Plan de travail (« Atelier », migration 043) ----------------------------

/** Un post-it : il porte un verbatim (`verbatimId`) ou une note libre (`text`). */
export interface BoardNote {
  id: string;
  verbatimId: string | null;
  text: string | null;
  x: number;
  y: number;
  groupId: string | null;
  /** L'étude d'où il vient : celle de son verbatim, ou celle de l'atelier où
   *  la note libre a été écrite. `null` : une note du projet. */
  studyId: string | null;
  createdAt: string;
}

/** Un regroupement de post-it. Pas de géométrie : son cadre se déduit de ses post-it. */
export interface BoardGroup {
  id: string;
  name: string;
  personaId: string | null;
  insightId: string | null;
  createdAt: string;
}

/** Un verbatim tel que le plan le lit — déjà posé ou encore à déposer. */
export interface BoardVerbatim {
  id: string;
  text: string;
  sentiment: Sentiment | null;
  gravity: Gravity | null;
  sourceRef: string | null;
  speaker: string | null;
  tags: string[];
  personaId: string | null;
  personaName: string | null;
  studyId: string | null;
  studyCode: string | null;
}

export interface Board {
  notes: BoardNote[];
  groups: BoardGroup[];
  verbatims: BoardVerbatim[];
}

// --- L'assistant de l'atelier : il propose, il n'écrit rien -------------------

/** `fake` : démonstration locale, sans modèle. */
export type AssistantMode = 'ia' | 'fake';

/** Un regroupement proposé ; `noteIds` sont des post-it réels du plan. */
export interface BoardClusterProposal {
  key: string;
  name: string;
  rationale: string | null;
  noteIds: string[];
}

/** Le brouillon de l'insight d'un groupe, à relire avant de l'enregistrer. */
export interface BoardInsightDraft {
  groupName: string | null;
  title: string | null;
  summary: string | null;
  why: string | null;
  opportunity: string | null;
  recommendation: string | null;
  recommendationKind: RecoKind | null;
  severity: string | null;
  problemType: ProblemType | null;
}

/** Ce qu'on envoie pour tirer un insight d'un groupe. */
export interface BoardInsightBody {
  title: string;
  summary?: string;
  why?: string;
  severity?: string;
  opportunity?: string;
  recommendation?: string;
  recommendationKind?: RecoKind;
  problemType?: ProblemType;
  origin?: 'manuel' | 'ia';
}
